/**
 * CSS-Selektoren fuer die Planer-Zeilen, deren `onboardingAnchor`-Attribut
 * **imperativ** gesetzt wird statt im Template zu stehen.
 *
 * Hintergrund: Die „Offene Anforderungen"-Zeile ist eine FullCalendar-Resource
 * mit negativer ID (REQUIREMENT_HEADER-Konvention). FullCalendar baut ihre
 * DOM-Knoten bei jedem Re-Render neu auf; ein im Template gesetztes Attribut
 * gibt es dort nicht. `work-shifts-planner-timeline.component.ts:3727-3741`
 * traegt es deshalb nach dem Rendern per `setAttribute` nach — und verliert es
 * bei jedem Neuaufbau wieder.
 *
 * Praktische Folge fuer Tour-Steps (gemessen 25.08.2026): Beim Wechsel vom
 * Muster- in den Mitarbeiter-Blick wird die Timeline-Komponente neu erzeugt
 * (`PlannerViewModeService`). Dabei entsteht ein Fenster von ~170 ms, in dem
 * die ZEILE schon existiert, das ANCHOR-ATTRIBUT aber noch nicht:
 *
 *   0,15 s  Zeile fehlt      · Attribut fehlt
 *   0,57 s  Zeile ist da     · Attribut fehlt   ← hier entscheidet ein
 *                                                 attribut-basiertes skipIf falsch
 *   0,74 s  Zeile ist da     · Attribut ist da
 *
 * Deshalb gilt fuer Steps, die an diesen Zeilen haengen, zweierlei:
 *   1. `skipIf` prueft den CSS-Selektor (= die Sache), nicht das Attribut
 *      (= ein fluechtiges Stellvertretermerkmal).
 *   2. `onBeforeShow` setzt das Attribut selbst neu, damit der Step nicht auf
 *      den Nachtrag der Komponente warten muss.
 *
 * `shift-assignment-help` machte das schon richtig und war die einzige Tour,
 * die in der Produktionsauswertung keinen Schritt verlor. Die Konstanten lagen
 * dort lokal und nicht exportiert; sie stehen hier, damit `mentor-endgame`
 * dieselben benutzt statt einer zweiten, driftenden Kopie.
 */

/** Label-Spalte links (`.fc-datagrid-body`) der „Offene Anforderungen"-Zeile. */
export const OPEN_REQ_ROW_SELECTOR = '.fc-datagrid-body tr[data-resource-id^="-"]';

/** Timeline-Lane rechts derselben Zeile (die Bedarfs-Kacheln). */
export const OPEN_REQ_LANE_SELECTOR = '.fc-timeline-lane[data-resource-id^="-"]';

/**
 * Setzt beide Anchor-Attribute auf die aktuell im DOM stehenden Zeilen.
 * Idempotent und absichtlich still: Fehlt die Zeile, passiert nichts — der
 * aufrufende Step hat dann ohnehin ein `skipIf` auf demselben Selektor.
 *
 * In `onBeforeShow` aufrufen, NICHT in `skipIf` (das laeuft laut
 * `tour-coordinator.service.ts:958` vor `onBeforeShow` und darf keine
 * Nebenwirkungen haben).
 */
export function reattachOpenRequirementsAnchors(): void {
  if (typeof document === 'undefined') return;
  document
    .querySelector(OPEN_REQ_ROW_SELECTOR)
    ?.setAttribute('onboardingAnchor', 'open-requirements-row');
  document
    .querySelector(OPEN_REQ_LANE_SELECTOR)
    ?.setAttribute('onboardingAnchor', 'open-requirements-lane');
}
